import { Box, ToggleButton } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { CommodityManagementApi } from '../../../../Apis/Apis';
import {
  CommodityModel,
  HedgePlanModel,
  HedgePlanOverHedgeLimit,
  HedgePlanSeasonModel,
  HedgePlanType,
  KnowRiskEnergyLocationModel,
  MatrixCalculationType,
  MatrixContractSettlement,
  MatrixSeasonType,
  MatrixStripType,
  UpdateHedgePlanRequest,
  VolumeSource,
} from '../../../../Generated/Commodity-Management-Api';
import { MarketViewProductModel } from '../../../../Generated/Mark-It-View-Api';
import { useLoadingState } from '../../../../Hooks';
import { StoneXDivider, StoneXRow } from '../../../StoneX';
import {
  StoneXAutocomplete,
  StoneXCheckbox,
  StoneXHelpLabel,
  StoneXLabeledItem,
  StoneXMultiAutocomplete,
  StoneXSectionedModal,
  StoneXSelect,
  StoneXTextField,
  StoneXToggleButton,
  StoneXToggleButtonGroup,
} from '../../../StoneXMui';
import { MONTH_NAMES, toSeasonPeriod } from './HedgePlanTerms';
import {
  CalculationTypeLabels,
  ContractSettlementLabels,
  OverHedgeLimitLabels,
  SeasonTypeLabels,
  StripTypeLabels,
  VolumeSourceLabels,
} from './HedgePlanLabels';
import { DefaultHedgePlanConfiguration, HedgePlanConfiguration } from './Types';

const preferredProductIds = [1];

interface HedgePlanSiteOption {
  siteGuid: string;
  siteName: string;
}

function SettingHelp(props: { summary: string; options: Array<{ label: string; description: string }> }) {
  return (
    <Box sx={{ maxWidth: 380 }}>
      <Box sx={{ marginBottom: 1 }}>{props.summary}</Box>
      {props.options.map((option) => (
        <Box key={option.label} sx={{ marginTop: '6px' }}>
          <strong>{option.label}:</strong> {option.description}
        </Box>
      ))}
    </Box>
  );
}

const calculationTypeHelp = (
  <SettingHelp
    summary="Controls when historical matrix observations are sampled to calculate the decile bands."
    options={[
      { label: 'Monthly', description: 'Samples an observation at the start of each month.' },
      { label: 'Expiration', description: 'Samples observations on futures contract expiration dates.' },
    ]}
  />
);

const contractSettlementHelp = (
  <SettingHelp
    summary="Controls when the price series rolls from one futures contract to the next."
    options={[
      { label: 'Expiration Date', description: 'Rolls on the product’s actual exchange expiration date.' },
      { label: 'Last Day of Month', description: 'Rolls at the calendar month boundary.' },
    ]}
  />
);

const stripTypeHelp = (
  <SettingHelp
    summary="Controls which futures contract or group of contracts supplies the market-price series."
    options={[
      { label: '1st Contract Month', description: 'Uses the nearest applicable contract.' },
      { label: '1st Contract Month (NG Settlement)', description: 'Uses the nearest applicable contract under the Natural Gas settlement convention.' },
      { label: '2nd Contract Month', description: 'Uses the contract immediately after the nearest applicable contract.' },
      { label: '12 Month Strip', description: 'Averages the next twelve forward contracts for each quote date and ignores seasonal divisions.' },
      { label: 'Forward Contract', description: 'Uses each fixed delivery contract’s own price history for the selected season instead of continuously rolling through nearby contracts. The series advances when the applicable contract period changes.' },
    ]}
  />
);

const ppiHelp = (
  <Box sx={{ maxWidth: 380 }}>
    Adjusts historical prices for inflation using the Producer Price Index (PPI), so older prices are compared in today's terms when the decile bands are calculated.
  </Box>
);

const fiscalStartMonthHelp = (
  <Box sx={{ maxWidth: 380 }}>
    Annual and Quarterly seasons follow the fiscal year: Q1 starts in this month. A fiscal year is named after the year it ends in, so with an August start, FY2027 Q1 is August to October 2026.
  </Box>
);

const overHedgeLimitHelp = (
  <SettingHelp
    summary="Controls when the hedge recommendation turns negative (i.e. tells you to reduce your position instead of add to it)."
    options={[
      { label: '100%', description: 'Once the current hedge percent passes 100%, the excess becomes a negative recommendation.' },
      { label: 'Max Target', description: 'Once the current hedge percent passes the total of the value trigger targets, the excess becomes a negative recommendation.' },
    ]}
  />
);

const planTypeHelp = (
  <SettingHelp
    summary="Controls what happens to triggers when the current season ends. Can't be changed after the plan is created."
    options={[
      { label: 'Static', description: 'Triggers belong to one season and drop off with it.' },
      { label: 'Rolling', description: 'Triggers belong to the season position. When the current season ends, the next season takes over its triggers, and time triggers keep the same number of days from the start of the season.' },
    ]}
  />
);

const volumeSourceHelp = (
  <SettingHelp
    summary="The budget the plan measures its hedges against. It sets what 100% is, so trades are entered as volumes, and it gives the season's budget price. It can also weight the price line by budgeted volume."
    options={[
      { label: 'None', description: 'No budget: trades are entered as percents, and every contract month counts equally.' },
      { label: 'Commodity Management', description: 'The budget for one commodity on a site.' },
      { label: 'Know Risk Energy', description: 'The budgets of KrCms locations under a customer.' },
    ]}
  />
);

interface HedgePlanModalProps {
  open: boolean;
  onClose: () => void;
  onSaved: (hedgePlan: HedgePlanModel) => void;
  companyGuid: string;
  existing?: HedgePlanModel;
  products: MarketViewProductModel[];
  sites: HedgePlanSiteOption[];
  // Loaded once per company by the page, so opening Settings doesn't refetch them.
  commoditiesBySiteGuid: Record<string, CommodityModel[]>;
  commoditiesLoading: boolean;
  // Hidden and ended seasons the Previous Seasons tab can show again for the session.
  hiddenSeasons: HedgePlanSeasonModel[];
  shownSeasonKeys: string[];
  onToggleSeasonShown: (periodKey: string) => void;
}

export default function HedgePlanModal(props: HedgePlanModalProps) {
  const { open, onClose, onSaved, companyGuid, existing, products, sites, commoditiesBySiteGuid, commoditiesLoading } = props;

  const existingConfiguration = useMemo<HedgePlanConfiguration | undefined>(
    () => (existing ? { ...DefaultHedgePlanConfiguration, ...JSON.parse(existing.configuration) as Partial<HedgePlanConfiguration> } : undefined),
    [existing],
  );

  const saveLoadingState = useLoadingState();

  const [name, setName] = useState<string>('');
  const [selectedProduct, setSelectedProduct] = useState<MarketViewProductModel | null>(null);
  const [planType, setPlanType] = useState<HedgePlanType>(HedgePlanType.Static);
  const [seasonType, setSeasonType] = useState<MatrixSeasonType>(MatrixSeasonType.Annual);
  const [fiscalStartMonth, setFiscalStartMonth] = useState<number>(1);
  const [calculationType, setCalculationType] = useState<MatrixCalculationType>(MatrixCalculationType.Monthly);
  const [contractSettlement, setContractSettlement] = useState<MatrixContractSettlement>(MatrixContractSettlement.ExpirationDate);
  const [stripType, setStripType] = useState<MatrixStripType>(MatrixStripType.RollingMonth);
  const [enableValueTriggers, setEnableValueTriggers] = useState<boolean>(true);
  const [enableBudgetTriggers, setEnableBudgetTriggers] = useState<boolean>(true);
  const [enableTimeTriggers, setEnableTimeTriggers] = useState<boolean>(true);
  const [overHedgeLimit, setOverHedgeLimit] = useState<HedgePlanOverHedgeLimit>(HedgePlanOverHedgeLimit.Hundred);
  const [showTimeTriggers, setShowTimeTriggers] = useState<boolean>(true);
  const [snapTradesToMarketPrice, setSnapTradesToMarketPrice] = useState<boolean>(false);
  const [selectedSite, setSelectedSite] = useState<HedgePlanSiteOption | null>(null);
  const [volumeSource, setVolumeSource] = useState<VolumeSource>(VolumeSource.None);
  const [weightPriceCurveByVolume, setWeightPriceCurveByVolume] = useState<boolean>(false);
  const [applyPpi, setApplyPpi] = useState<boolean>(true);

  // Commodity Management volume source: one commodity, scoped to the selected site.
  const [selectedCommodity, setSelectedCommodity] = useState<CommodityModel | null>(null);

  // Know Risk Energy volume source: locations, scoped to the KrCms customer linked to the
  // company (Company.KnowRiskEnergyCustomerId in Mv3). Every company is linked, so locations
  // load automatically off of companyGuid with no customer picker.
  const [knowRiskEnergyLocations, setKnowRiskEnergyLocations] = useState<KnowRiskEnergyLocationModel[]>([]);
  const [selectedKnowRiskEnergyLocations, setSelectedKnowRiskEnergyLocations] = useState<KnowRiskEnergyLocationModel[]>([]);
  const knowRiskEnergyLocationsLoadingState = useLoadingState();

  // A 12 Month Strip on a seasonal plan makes the matrix ignore the season breakdown (see the
  // note below the Strip Type field), so a volume-weighted price line would be weighting months
  // the matrix isn't actually pricing separately.
  const volumeSourceDisabled = stripType === MatrixStripType.Strip && seasonType !== MatrixSeasonType.Annual;

  useEffect(() => {
    if (volumeSourceDisabled) {
      setVolumeSource(VolumeSource.None);
      setWeightPriceCurveByVolume(false);
      setSelectedSite(null);
      setSelectedCommodity(null);
      setSelectedKnowRiskEnergyLocations([]);
    }
  }, [volumeSourceDisabled]);

  // Only fires from the user picking a different Volume Source (see updateVolumeSource) — never
  // from loading an existing plan — so it can't race with that load effect setting selectedSite
  // from existing.siteGuid.
  function updateVolumeSource(value: VolumeSource | null) {
    const next = value ?? VolumeSource.None;
    setVolumeSource(next);

    if (next === VolumeSource.None) {
      setWeightPriceCurveByVolume(false);
    }

    if (next !== VolumeSource.CommodityManagement) {
      setSelectedSite(null);
      setSelectedCommodity(null);
    }

    if (next !== VolumeSource.KnowRiskEnergy) {
      setSelectedKnowRiskEnergyLocations([]);
    }
  }

  const commodities = useMemo(
    () => (selectedSite ? commoditiesBySiteGuid[selectedSite.siteGuid] ?? [] : []),
    [selectedSite, commoditiesBySiteGuid],
  );

  // Pre-select the plan's saved commodity only if we're still looking at the same site it was saved against.
  useEffect(() => {
    const existingCommodityId = existing?.siteGuid === selectedSite?.siteGuid ? existing?.volumeSourceIds?.[0] : undefined;
    setSelectedCommodity(commodities.find((commodity) => commodity.commodityId === existingCommodityId) ?? null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, commodities]);

  // Loads locations for the company's linked KrCms customer once Know Risk Energy is selected,
  // and pre-checks whichever of the plan's saved location ids come back.
  useEffect(() => {
    if (!open || volumeSource !== VolumeSource.KnowRiskEnergy || knowRiskEnergyLocations.length > 0) {
      return;
    }

    CommodityManagementApi.getLocationsForCompany(knowRiskEnergyLocationsLoadingState.setLoadingState, companyGuid).then((response) => {
      const rows = response.data.rows ?? [];
      setKnowRiskEnergyLocations(rows);

      if (existing?.volumeSource === VolumeSource.KnowRiskEnergy) {
        const existingIds = new Set(existing.volumeSourceIds ?? []);
        setSelectedKnowRiskEnergyLocations(rows.filter((location) => location.id !== undefined && existingIds.has(location.id)));
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, volumeSource]);

  const orderedProducts = useMemo(
    () => [
      ...products.filter((product) => preferredProductIds.includes(product.productId)),
      ...products.filter((product) => !preferredProductIds.includes(product.productId)),
    ],
    [products],
  );
  const preferredProductCount = orderedProducts.filter((product) => preferredProductIds.includes(product.productId)).length;
  const showPreferredProductDivider = preferredProductCount > 0 && preferredProductCount < orderedProducts.length;

  useEffect(() => {
    if (!open) {
      return;
    }
    const configuration = existingConfiguration ?? DefaultHedgePlanConfiguration;
    setEnableValueTriggers(configuration.enableValueTriggers);
    setEnableBudgetTriggers(configuration.enableBudgetTriggers);
    setEnableTimeTriggers(configuration.enableTimeTriggers);
    setShowTimeTriggers(configuration.chartSettings.showTimeTriggers);
    setSnapTradesToMarketPrice(configuration.chartSettings.snapTradesToMarketPrice);

    if (existing) {
      const existingProduct = products.find((product) => product.productId === existing.marketViewProductId) ?? null;
      setName(existing.name?.trim().length > 0 ? existing.name : existingProduct?.name ?? '');
      setPlanType(existing.planType);
      setSeasonType(existing.seasonType);
      setFiscalStartMonth(existing.fiscalStartMonth);
      setCalculationType(existing.calculationType);
      setContractSettlement(existing.contractSettlement);
      setStripType(existing.stripType);
      setSelectedProduct(existingProduct);
      setSelectedSite(sites.find((site) => site.siteGuid === existing.siteGuid)
        ?? (sites.length === 1 ? sites[0] : null));
      setVolumeSource(existing.volumeSource ?? VolumeSource.None);
      setWeightPriceCurveByVolume(existing.weightPriceCurveByVolume ?? false);
      setOverHedgeLimit(existing.overHedgeLimit ?? HedgePlanOverHedgeLimit.Hundred);
      setApplyPpi(existing.applyPpi ?? true);
    } else {
      setName('');
      setPlanType(HedgePlanType.Static);
      setSeasonType(MatrixSeasonType.Annual);
      setFiscalStartMonth(1);
      setCalculationType(MatrixCalculationType.Monthly);
      setContractSettlement(MatrixContractSettlement.ExpirationDate);
      setStripType(MatrixStripType.RollingMonth);
      setSelectedProduct(null);
      setSelectedSite(sites.length === 1 ? sites[0] : null);
      setVolumeSource(VolumeSource.None);
      setWeightPriceCurveByVolume(false);
      setOverHedgeLimit(HedgePlanOverHedgeLimit.Hundred);
      setApplyPpi(true);
    }
  }, [open, existing, existingConfiguration, products, sites]);

  function updateSelectedSite(site: HedgePlanSiteOption | null) {
    setSelectedSite(site);
  }

  const volumeSourceIds = volumeSource === VolumeSource.CommodityManagement
    ? (selectedCommodity ? [selectedCommodity.commodityId] : [])
    : volumeSource === VolumeSource.KnowRiskEnergy
      ? selectedKnowRiskEnergyLocations
        .map((location) => location.id)
        .filter((id): id is number => id !== undefined)
      : [];

  const volumeSourceIdsInvalid = volumeSource !== VolumeSource.None && volumeSourceIds.length === 0;

  function save() {
    if (!selectedProduct || name.trim().length === 0 || volumeSourceIdsInvalid) {
      return;
    }

    const configuration: HedgePlanConfiguration = {
      enableValueTriggers,
      enableBudgetTriggers,
      enableTimeTriggers,
      chartSettings: { showTimeTriggers, snapTradesToMarketPrice },
    };

    const request: UpdateHedgePlanRequest = {
      companyGuid,
      hedgePlanId: existing?.hedgePlanId,
      name: name.trim(),
      marketViewProductId: selectedProduct.productId,
      planType,
      seasonType,
      fiscalStartMonth,
      calculationType,
      contractSettlement,
      stripType,
      siteGuid: selectedSite?.siteGuid ?? null,
      volumeSource: volumeSource === VolumeSource.None ? undefined : volumeSource,
      volumeSourceIds: volumeSource === VolumeSource.None ? null : volumeSourceIds,
      weightPriceCurveByVolume: volumeSource !== VolumeSource.None && weightPriceCurveByVolume,
      overHedgeLimit,
      applyPpi,
      configuration: JSON.stringify(configuration),
    };

    CommodityManagementApi.updateHedgePlan(saveLoadingState.setLoadingState, request).then((response) => {
      if (response.data.hedgePlan) {
        onSaved(response.data.hedgePlan);
      }
    });
  }

  function updateSelectedProduct(product: MarketViewProductModel | null) {
    setSelectedProduct(product);
    if (product) {
      setName((currentName) => currentName.trim().length === 0 ? product.name : currentName);
    }
  }

  const generalValid = !!selectedProduct && name.trim().length > 0;

  const generalSection = (
    <>
      <StoneXRow>
        <StoneXAutocomplete
          label="Market View Product"
          options={orderedProducts}
          value={selectedProduct}
          getOptionLabel={(product) => product.name}
          isOptionEqualToValue={(a, b) => a.productId === b.productId}
          renderOption={(optionProps, product, state) => (
            <li
              {...optionProps}
              style={showPreferredProductDivider && state.index === preferredProductCount - 1
                ? { ...optionProps.style, borderBottom: '1px solid var(--box-border-color)' }
                : optionProps.style}
            >
              {product.name}
            </li>
          )}
          onUpdate={updateSelectedProduct}
          placeholder="Select a product"
          showRequired
          fullWidth
        />
      </StoneXRow>

      <StoneXRow>
        <StoneXTextField
          label="Plan Name"
          value={name}
          onUpdate={setName}
          placeholder="e.g. Corn Winter Program"
          showRequired
          fullWidth
        />
      </StoneXRow>

      {seasonType !== MatrixSeasonType.WinterSummer && (
        <StoneXRow>
          <StoneXSelect
            label={<StoneXHelpLabel label="Fiscal Start Month" helpText={fiscalStartMonthHelp} />}
            value={fiscalStartMonth}
            options={MONTH_NAMES.map((_, index) => index + 1)}
            getOptionLabel={(month: number) => MONTH_NAMES[month - 1]}
            getId={(month: number) => month}
            onChange={(value) => setFiscalStartMonth(value ?? 1)}
            fullWidth
          />
        </StoneXRow>
      )}

      <StoneXRow>
        <StoneXToggleButtonGroup
          label={<StoneXHelpLabel label="Plan Type" helpText={planTypeHelp} />}
          exclusive
          value={planType}
          disableTextTransform
          disabled={!!existing}
          onChange={(_e, value) => value !== null && setPlanType(value as HedgePlanType)}
        >
          {Object.values(HedgePlanType).map((option) => (
            <ToggleButton key={option} value={option}>{option}</ToggleButton>
          ))}
        </StoneXToggleButtonGroup>
      </StoneXRow>
    </>
  );

  const matrixSection = (
    <>
      <StoneXRow>
        <StoneXToggleButtonGroup
          label="Season"
          exclusive
          value={seasonType}
          disableTextTransform
          onChange={(_e, value) => value !== null && setSeasonType(value as MatrixSeasonType)}
        >
          {Object.values(MatrixSeasonType).map((option) => (
            <ToggleButton key={option} value={option}>{SeasonTypeLabels[option]}</ToggleButton>
          ))}
        </StoneXToggleButtonGroup>
      </StoneXRow>

      <StoneXRow>
        <StoneXToggleButtonGroup
          label={<StoneXHelpLabel label="Calculation Type" helpText={calculationTypeHelp} />}
          exclusive
          value={calculationType}
          disableTextTransform
          onChange={(_e, value) => value !== null && setCalculationType(value as MatrixCalculationType)}
        >
          {Object.values(MatrixCalculationType).map((option) => (
            <ToggleButton key={option} value={option}>{CalculationTypeLabels[option]}</ToggleButton>
          ))}
        </StoneXToggleButtonGroup>
      </StoneXRow>

      <StoneXRow>
        <StoneXToggleButtonGroup
          label={<StoneXHelpLabel label="Contract Settlement" helpText={contractSettlementHelp} />}
          exclusive
          value={contractSettlement}
          disableTextTransform
          onChange={(_e, value) => value !== null && setContractSettlement(value as MatrixContractSettlement)}
        >
          {Object.values(MatrixContractSettlement).map((option) => (
            <ToggleButton key={option} value={option}>{ContractSettlementLabels[option]}</ToggleButton>
          ))}
        </StoneXToggleButtonGroup>
      </StoneXRow>

      <StoneXRow>
        <StoneXSelect
          label={<StoneXHelpLabel label="Strip Type" helpText={stripTypeHelp} />}
          value={stripType}
          options={Object.values(MatrixStripType)}
          getOptionLabel={(option: MatrixStripType) => StripTypeLabels[option]}
          getId={(option: MatrixStripType) => option}
          onChange={(value) => setStripType(value ?? MatrixStripType.RollingMonth)}
          fullWidth
        />
      </StoneXRow>

      {stripType === MatrixStripType.Strip && seasonType !== MatrixSeasonType.Annual && (
        <StoneXRow>
          <span style={{ color: 'var(--gray4)' }}>
            Strip averages twelve forward contracts, so the matrix will ignore the season breakdown.
          </span>
        </StoneXRow>
      )}

      <StoneXRow>
        <StoneXToggleButton label={<StoneXHelpLabel label="Apply PPI" helpText={ppiHelp} />} value={applyPpi} onChange={setApplyPpi} />
      </StoneXRow>
    </>
  );

  const volumeSection = (
    <>
      <StoneXRow>
        <StoneXSelect
          label={<StoneXHelpLabel label="Budget Source" helpText={volumeSourceHelp} />}
          value={volumeSource}
          options={Object.values(VolumeSource)}
          getOptionLabel={(option: VolumeSource) => VolumeSourceLabels[option]}
          getId={(option: VolumeSource) => option}
          onChange={updateVolumeSource}
          disabled={volumeSourceDisabled}
          fullWidth
        />
      </StoneXRow>

      {volumeSourceDisabled && (
        <StoneXRow>
          <span style={{ color: 'var(--gray4)' }}>
            Not available with 12 Month Strip on a seasonal plan — the matrix ignores the season breakdown.
          </span>
        </StoneXRow>
      )}

      {!volumeSourceDisabled && volumeSource === VolumeSource.CommodityManagement && (
        <>
          {sites.length > 1 && (
            <StoneXRow>
              <StoneXAutocomplete
                label="Site"
                options={sites}
                value={selectedSite}
                getOptionLabel={(site: HedgePlanSiteOption) => site.siteName}
                isOptionEqualToValue={(a: HedgePlanSiteOption, b: HedgePlanSiteOption) => a.siteGuid === b.siteGuid}
                onUpdate={updateSelectedSite}
                placeholder="Select a site"
                showRequired
                fullWidth
              />
            </StoneXRow>
          )}

          {selectedSite && (
            <StoneXRow>
              <StoneXAutocomplete
                label="Commodity"
                options={commodities}
                loading={() => commoditiesLoading}
                value={selectedCommodity}
                getOptionLabel={(commodity: CommodityModel) => commodity.commodityName}
                isOptionEqualToValue={(a: CommodityModel, b: CommodityModel) => a.commodityId === b.commodityId}
                onUpdate={setSelectedCommodity}
                placeholder="Select a commodity"
                error={volumeSourceIdsInvalid ? 'Select a commodity' : undefined}
                showRequired
                fullWidth
              />
            </StoneXRow>
          )}
        </>
      )}

      {!volumeSourceDisabled && volumeSource === VolumeSource.KnowRiskEnergy && (
        <StoneXRow>
          <StoneXMultiAutocomplete
            label="Locations"
            options={knowRiskEnergyLocations}
            loading={knowRiskEnergyLocationsLoadingState.isLoading}
            value={selectedKnowRiskEnergyLocations}
            getOptionLabel={(location: KnowRiskEnergyLocationModel) => location.name ?? ''}
            onUpdate={setSelectedKnowRiskEnergyLocations}
            placeholder="Select location(s)"
            width="100%"
          />
        </StoneXRow>
      )}

      {!volumeSourceDisabled && volumeSource !== VolumeSource.None && (
        <StoneXRow>
          <StoneXCheckbox
            label="Weight price curve by budgeted volume by default"
            checked={weightPriceCurveByVolume}
            onUpdate={setWeightPriceCurveByVolume}
          />
        </StoneXRow>
      )}

    </>
  );

  const previousSeasonsSection = (
    <>
      <StoneXRow>
        <span style={{ color: 'var(--gray4)' }}>
          Shows a hidden or ended season in the hedges table until you leave the page. To bring a hidden season back for good, use Unhide in its season settings.
        </span>
      </StoneXRow>
      {props.hiddenSeasons.length === 0 ? (
        <StoneXRow>No hidden or ended seasons.</StoneXRow>
      ) : props.hiddenSeasons.map((season) => {
        const periodKey = toSeasonPeriod(season).key;
        return (
          <StoneXRow key={periodKey}>
            <StoneXCheckbox
              label={`${season.label} (${season.isHidden ? 'hidden' : 'ended'})`}
              checked={props.shownSeasonKeys.includes(periodKey)}
              onUpdate={() => props.onToggleSeasonShown(periodKey)}
            />
          </StoneXRow>
        );
      })}
    </>
  );

  const triggersAndChartSection = (
    <>
      <StoneXRow>
        <StoneXLabeledItem label="Trigger Types" fullWidth>
          <Box sx={{ display: 'flex', flexWrap: 'nowrap', gap: 1 }}>
            <StoneXCheckbox label="Value" checked={enableValueTriggers} onUpdate={setEnableValueTriggers} />
            <StoneXCheckbox label="Budget" checked={enableBudgetTriggers} onUpdate={setEnableBudgetTriggers} />
            <StoneXCheckbox label="Time" checked={enableTimeTriggers} onUpdate={setEnableTimeTriggers} />
          </Box>
        </StoneXLabeledItem>
      </StoneXRow>

      <StoneXRow>
        <StoneXToggleButtonGroup
          label={<StoneXHelpLabel label="Over-Hedge Limit" helpText={overHedgeLimitHelp} />}
          exclusive
          value={overHedgeLimit}
          disableTextTransform
          onChange={(_e, value) => value !== null && setOverHedgeLimit(value as HedgePlanOverHedgeLimit)}
        >
          {Object.values(HedgePlanOverHedgeLimit).map((option) => (
            <ToggleButton key={option} value={option}>{OverHedgeLimitLabels[option]}</ToggleButton>
          ))}
        </StoneXToggleButtonGroup>
      </StoneXRow>

      <StoneXDivider />

      <StoneXRow>
        <StoneXLabeledItem label="Chart Display" fullWidth>
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <StoneXCheckbox label="Show time triggers" checked={showTimeTriggers} onUpdate={setShowTimeTriggers} />
            <StoneXCheckbox label="Snap trades to market price" checked={snapTradesToMarketPrice} onUpdate={setSnapTradesToMarketPrice} />
          </Box>
        </StoneXLabeledItem>
      </StoneXRow>
    </>
  );

  return (
    <StoneXSectionedModal
      header={existing ? 'Edit Hedge Plan' : 'New Hedge Plan'}
      open={open}
      onClose={onClose}
      mode={existing ? 'tabs' : 'steps'}
      onSave={save}
      isSaving={saveLoadingState.isLoading()}
      width="520px"
      sections={[
        { label: 'General', content: generalSection, valid: generalValid },
        { label: 'Matrix', content: matrixSection },
        { label: 'Budget', content: volumeSection, valid: !volumeSourceIdsInvalid },
        { label: 'Triggers & Chart', content: triggersAndChartSection },
        ...(existing ? [{ label: 'Previous Seasons', content: previousSeasonsSection }] : []),
      ]}
    />
  );
}
